import { captureExceptionWithContext } from 'services/captureException';
import filterPlateTypeCheckers from 'types/filterPlateTypeCheckers';
import isEntityInformationTable from 'types/visTypeCheckers/isEntityInformationTable';
import isGauge from 'types/visTypeCheckers/isGauge';
import isGroupVsRemainder from 'types/visTypeCheckers/isGroupVsRemainder';
import isPaceMatrix from 'types/visTypeCheckers/isPaceMatrix';
import isRankingMatrix from 'types/visTypeCheckers/isRankingMatrix';
import isRemindersGadget from 'types/visTypeCheckers/isRemindersGadget';
import isSingleMetricDateMatrix from 'types/visTypeCheckers/isSingleMetricDateMatrix';
import isV5ChartDef from 'types/visTypeCheckers/isV5ChartDef';

const isFilterUsedInChart = (
  filter: SavedFilter,
  // Undefined for a gadget saved without a chart definition
  def: VisualisationDefinition | undefined,
  getIsUsedInMetricId: ({
    filter,
    metricId,
  }: {
    filter: SavedFilter;
    metricId: string;
  }) => boolean,
) => {
  if (!def) {
    return false;
  }

  if (isV5ChartDef(def)) {
    return def.series.some(
      (s) =>
        getIsUsedInMetricId({ filter, metricId: s.metricId }) ||
        // A series asking its own question can filter by it too
        ((s.queryOverrides && s.queryOverrides.filters) || []).some(
          (plate) =>
            filterPlateTypeCheckers.isSavedFilter(plate) &&
            plate.datasetFilterId === filter.id,
        ),
    );
  } else if (isSingleMetricDateMatrix(def)) {
    return getIsUsedInMetricId({ filter, metricId: def.metricId });
  } else if (isRankingMatrix(def)) {
    return def.metrics.some((m) =>
      getIsUsedInMetricId({ filter, metricId: m.metricId }),
    );
  } else if (isGauge(def)) {
    return getIsUsedInMetricId({ filter, metricId: def.metricId });
  } else if (isRemindersGadget(def)) {
    return false;
  } else if (isPaceMatrix(def)) {
    return getIsUsedInMetricId({ filter, metricId: def.metricId });
  } else if (isGroupVsRemainder(def)) {
    return false;
  } else if (isEntityInformationTable(def)) {
    return def.rows.some(
      (r) =>
        r.type === 'metric' &&
        getIsUsedInMetricId({ filter, metricId: r.metricId }),
    );
  } else {
    // Fails to compile when a new definition type is added but not handled
    // here. At runtime this only catches bad data.
    const unhandled: never = def;
    const { type, id } = unhandled as { type?: string; id?: string };
    const e = new Error(`type: ${type}`);
    e.name = `Unknown definition type`;
    captureExceptionWithContext({
      error: e,
      extra: { type, id },
      tags: { visType: type },
    });
    return false;
  }
};

export default isFilterUsedInChart;
